import axios from 'axios';
import { Order, OrderStatus } from '../types';
import { userPool } from '../lib/cognito';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/dev';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

apiClient.interceptors.request.use(async (config) => {
  const user = userPool.getCurrentUser();
  if (user) {
    const token = await new Promise<string | null>((resolve) => {
      user.getSession((err: Error | null, session: { isValid: () => boolean; getIdToken: () => { getJwtToken: () => string } } | null) => {
        if (err || !session?.isValid()) return resolve(null);
        resolve(session.getIdToken().getJwtToken());
      });
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ── Orders API ─────────────────────────────────────────────────────────────────
export const ordersApi = {
  list: async (restaurantId: string, status?: OrderStatus): Promise<Order[]> => {
    const params: Record<string, string> = { restaurantId };
    if (status) params.status = status;
    const { data } = await apiClient.get('/orders', { params });
    return data.data.orders;
  },

  create: async (payload: {
    restaurantId: string;
    kioskId: string;
    items: { itemId: string; name: string; price: number; quantity: number }[];
    paymentMethod: string;
    customerName?: string;
    notes?: string;
  }): Promise<Order> => {
    const { data } = await apiClient.post('/orders', payload);
    return data.data;
  },

  updateStatus: async (
    orderId: string,
    restaurantId: string,
    status: OrderStatus
  ): Promise<Order> => {
    const { data } = await apiClient.patch(`/orders/${orderId}`, {
      restaurantId,
      status,
    });
    return data.data;
  },
};

// ── Menu API ───────────────────────────────────────────────────────────────────
export interface MenuItem {
  restaurantId: string;
  itemId: string;
  name: string;
  category: string;
  price: number;
  description?: string;
  calories?: number;
  tags?: string[];
  imageUrl?: string;
  isAvailable: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const menuApi = {
  list: async (restaurantId: string): Promise<{ items: MenuItem[]; byCategory: Record<string, MenuItem[]>; categories: string[] }> => {
    const { data } = await apiClient.get('/menu', { params: { restaurantId } });
    return data.data;
  },

  create: async (restaurantId: string, item: Omit<MenuItem, 'restaurantId' | 'itemId' | 'createdAt' | 'updatedAt'>): Promise<MenuItem> => {
    const { data } = await apiClient.post('/menu', { restaurantId, ...item });
    return data.data;
  },

  update: async (restaurantId: string, itemId: string, patch: Partial<MenuItem>): Promise<MenuItem> => {
    const { data } = await apiClient.put(`/menu/${itemId}`, { restaurantId, ...patch });
    return data.data;
  },

  delete: async (restaurantId: string, itemId: string): Promise<void> => {
    await apiClient.delete(`/menu/${itemId}`, { data: { restaurantId } });
  },

  getCategories: async (restaurantId: string): Promise<string[]> => {
    const { data } = await apiClient.get('/menu/categories', { params: { restaurantId } });
    return data.data.categories;
  },

  updateCategories: async (restaurantId: string, categories: string[], renamedFrom?: string, renamedTo?: string): Promise<string[]> => {
    const { data } = await apiClient.put('/menu/categories', { restaurantId, categories, renamedFrom, renamedTo });
    return data.data.categories;
  },

  getUploadUrl: async (restaurantId: string, fileName: string, contentType: string): Promise<{ uploadUrl: string; imageUrl: string }> => {
    const { data } = await apiClient.post('/menu/upload-url', { restaurantId, fileName, contentType });
    return data.data;
  },

  uploadImageToS3: async (uploadUrl: string, file: File): Promise<void> => {
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
  },
};

// ── Admin API ─────────────────────────────────────────────────────────────────

export interface RestaurantSummary {
  restaurantId: string;
  name: string;
  address: string;
  phone?: string;
  createdAt: string;
  menuItemCount?: number;
  orderCount?: number;
}

export interface CreateRestaurantPayload {
  restaurantId: string;
  name: string;
  address: string;
  phone?: string;
  taxRate: number;
  staffUsername: string;
  staffPassword: string;
}

export const adminApi = {
  listRestaurants: async (): Promise<RestaurantSummary[]> => {
    const { data } = await apiClient.get('/admin/restaurants');
    return data.data.restaurants;
  },

  createRestaurant: async (payload: CreateRestaurantPayload): Promise<{ restaurant: RestaurantSummary; cognitoUser: string }> => {
    const { data } = await apiClient.post('/admin/restaurants', payload);
    return data.data;
  },
};
